import { FC, KeyboardEvent, useId } from "react";
import { Box, CircularProgress, Typography } from "@mui/material";
import { useTranslation } from "react-i18next";
import { OverflowTooltip } from "src/domains/shared/components/OverflowTooltip";
import { COLOR_CONSTANTS } from "src/domains/shared/constants/styles";
import {
  FIELD_BORDER,
  FIELD_ERROR_POP_SX,
  FOCUS_RING,
  HOVER_SURFACE,
  HOVER_TRANSITION_MS,
  LineGlyph,
  REDUCED_MOTION_SX,
  SkeletonBone,
  swapFadeCss,
} from "src/domains/shared/components/StateTransition";
import { ValueSelect } from "src/domains/shared/components/ValueSelect";
import {
  MYPAGE_EMPTY_VALUE,
  MYPAGE_ROW_DIVIDER,
  MYPAGE_ROW_HOVER_SURFACE,
  MYPAGE_SECONDARY_ICON,
} from "../myPageStyles";

export type AccountInfoRowMode = "view" | "edit" | "loading" | "failed";

export type AccountInfoRowInput = { kind: "text"; inputMode?: "text" | "numeric" } | { kind: "select"; options: string[] };

interface AccountInfoRowProps {
  label: string;
  value: string;
  mode: AccountInfoRowMode;
  /** 수정할 수 있는 항목인지 — 이메일·표시 시간대는 false. */
  editable: boolean;
  /** 다른 줄을 편집 중이면 이 줄의 수정 버튼을 잠근다. */
  editLocked: boolean;
  skeletonWidth: string;
  /** 편집 입력 — 텍스트 또는 선택 목록. */
  input?: AccountInfoRowInput;
  draft: string;
  errorText: string | null;
  saving: boolean;
  onEdit: () => void;
  onDraftChange: (value: string) => void;
  onConfirm: () => void;
  onCancel: () => void;
}

/** 보기·편집 모두 같은 높이 — 편집을 시작해도 아래 줄이 움직이지 않는다. */
const ROW_HEIGHT_PX = 48;
const ACTIONS_WIDTH_PX = 64;

/** 계정 정보 한 줄 — 보기·편집·로딩·실패를 같은 칸에서 바꾼다. */
export const AccountInfoRow: FC<AccountInfoRowProps> = ({
  label,
  value,
  mode,
  editable,
  editLocked,
  skeletonWidth,
  input,
  draft,
  errorText,
  saving,
  onEdit,
  onDraftChange,
  onConfirm,
  onCancel,
}) => {
  const { t } = useTranslation("projects");
  const errorId = useId();
  const isEditing = mode === "edit";

  const handleKeyDown = (event: KeyboardEvent<HTMLElement>) => {
    if (event.key === "Enter") {
      event.preventDefault();
      onConfirm();
    }
    if (event.key === "Escape") {
      event.preventDefault();
      event.stopPropagation();
      onCancel();
    }
  };

  const inputA11y = {
    "aria-label": label,
    "aria-invalid": errorText ? true : undefined,
    "aria-describedby": errorText ? errorId : undefined,
    "aria-required": true,
    autoFocus: true,
    disabled: saving,
    onKeyDown: handleKeyDown,
  };

  const valueCell = (() => {
    if (mode === "loading") return <SkeletonBone width={skeletonWidth} height={12} />;
    if (mode === "failed") {
      return (
        <Typography
          css={swapFadeCss}
          aria-label={t("myPageModal.account.emptyValue", { ns: "projects" })}
          sx={{ ...valueTextSx, color: MYPAGE_EMPTY_VALUE }}
        >
          —
        </Typography>
      );
    }
    if (isEditing && input?.kind === "select") {
      // 목록이 열려 있을 때의 Enter·Esc 는 콤보박스가 처리하고, 닫힌 상태의 Esc 만 편집 취소로 올라온다.
      return (
        <Box
          css={swapFadeCss}
          onKeyDown={(event: KeyboardEvent<HTMLElement>) => {
            if (event.key !== "Escape") return;
            event.preventDefault();
            event.stopPropagation();
            onCancel();
          }}
          sx={{ display: "flex" }}
        >
          <ValueSelect
            ariaLabel={label}
            options={input.options.map((option) => ({ value: option, label: option }))}
            value={draft}
            onChange={onDraftChange}
            disabled={saving}
            placeholder={label}
            menuPlacement="top"
            autoFocus
          />
        </Box>
      );
    }
    if (isEditing) {
      return (
        <Box
          component="input"
          css={swapFadeCss}
          type="text"
          inputMode={input?.kind === "text" ? input.inputMode : undefined}
          {...inputA11y}
          value={draft}
          onChange={(event) => onDraftChange((event.target as HTMLInputElement).value)}
          sx={fieldSx(Boolean(errorText))}
        />
      );
    }
    return (
      <OverflowTooltip
        text={value}
        placement="bottom-start"
        sx={{ ...valueTextSx, color: editable ? COLOR_CONSTANTS.text.primary : COLOR_CONSTANTS.text.secondary }}
      />
    );
  })();

  const actionsCell = (() => {
    if (mode !== "view" && mode !== "edit") return null;
    if (isEditing && saving) {
      return (
        <Box role="status" aria-label={t("myPageModal.account.saving", { ns: "projects" })} sx={{ ...iconSlotSx, width: ACTIONS_WIDTH_PX, color: COLOR_CONSTANTS.primary.main }}>
          <CircularProgress size={14} thickness={5} color="inherit" />
        </Box>
      );
    }
    if (isEditing) {
      return (
        <>
          <Box
            component="button"
            type="button"
            css={swapFadeCss}
            aria-label={t("myPageModal.account.cancelEdit", { ns: "projects", field: label })}
            onClick={onCancel}
            sx={iconButtonSx(COLOR_CONSTANTS.text.secondary)}
          >
            <LineGlyph name="close" />
          </Box>
          <Box
            component="button"
            type="button"
            css={swapFadeCss}
            aria-label={t("myPageModal.account.saveField", { ns: "projects", field: label })}
            onClick={onConfirm}
            sx={iconButtonSx(COLOR_CONSTANTS.primary.main)}
          >
            <LineGlyph name="check" strokeWidth={2.2} />
          </Box>
        </>
      );
    }
    if (!editable) return null;
    return (
      <Box
        component="button"
        type="button"
        aria-label={t("myPageModal.account.editField", { ns: "projects", field: label })}
        disabled={editLocked}
        onClick={onEdit}
        sx={iconButtonSx(MYPAGE_SECONDARY_ICON)}
      >
        <LineGlyph name="pencil" />
      </Box>
    );
  })();

  return (
    <Box
      sx={{
        height: ROW_HEIGHT_PX,
        borderTop: MYPAGE_ROW_DIVIDER,
        backgroundColor: isEditing ? COLOR_CONSTANTS.primary._states.selected : "transparent",
        transition: `background-color ${HOVER_TRANSITION_MS}ms ease-out`,
        // 카드가 오류 표시·목록을 자르지 않도록 overflow 를 열어 두었으므로 줄 바탕이 카드 모서리를 따르게 한다.
        "&:first-of-type": { borderTop: 0, borderTopLeftRadius: "inherit", borderTopRightRadius: "inherit" },
        "&:last-of-type": { borderBottomLeftRadius: "inherit", borderBottomRightRadius: "inherit" },
        "&:hover": isEditing ? undefined : { backgroundColor: MYPAGE_ROW_HOVER_SURFACE },
        ...REDUCED_MOTION_SX,
      }}
    >
      <Box
        sx={{
          height: ROW_HEIGHT_PX,
          display: "grid",
          gridTemplateColumns: `112px minmax(0, 1fr) ${ACTIONS_WIDTH_PX}px`,
          alignItems: "center",
          gap: "8px",
          padding: "0 8px 0 16px",
        }}
      >
        <Typography sx={{ fontSize: 12, fontWeight: 500, color: COLOR_CONSTANTS.text.secondary }}>{label}</Typography>
        <Box sx={{ position: "relative", minWidth: 0 }}>
          {valueCell}
          {/* 줄 높이를 늘리지 않도록 입력칸 아래에 겹쳐 띄운다. */}
          {isEditing && errorText && (
            <Typography id={errorId} role="alert" sx={FIELD_ERROR_POP_SX}>
              {errorText}
            </Typography>
          )}
        </Box>
        <Box sx={{ display: "flex", justifyContent: "flex-end", gap: "4px" }}>{actionsCell}</Box>
      </Box>
    </Box>
  );
};

const valueTextSx = {
  fontSize: 13,
  fontWeight: 500,
  lineHeight: 1.5,
} as const;

const fieldSx = (hasError: boolean) => ({
  width: "100%",
  height: 32,
  padding: "0 10px",
  borderRadius: "6px",
  border: `1px solid ${hasError ? COLOR_CONSTANTS.error.main : COLOR_CONSTANTS.primary.main}`,
  boxShadow: hasError ? "none" : FOCUS_RING,
  outline: "none",
  backgroundColor: COLOR_CONSTANTS.common.white_states.main,
  fontFamily: "inherit",
  fontSize: 13,
  fontWeight: 500,
  color: COLOR_CONSTANTS.text.primary,
  transition: "border-color 150ms ease, box-shadow 150ms ease",
  "&:disabled": { opacity: 0.6 },
  "&:not(:focus)": hasError ? undefined : { borderColor: FIELD_BORDER, boxShadow: "none" },
  ...REDUCED_MOTION_SX,
});

const iconSlotSx = {
  height: 28,
  display: "inline-flex",
  alignItems: "center",
  justifyContent: "center",
} as const;

const iconButtonSx = (color: string) => ({
  ...iconSlotSx,
  width: 28,
  padding: 0,
  border: 0,
  borderRadius: "6px",
  backgroundColor: "transparent",
  color,
  cursor: "pointer",
  transition: "background-color 120ms ease, opacity 120ms ease",
  "&:hover:not(:disabled)": { backgroundColor: HOVER_SURFACE },
  "&:focus-visible": { outline: "none", boxShadow: `0 0 0 2px ${COLOR_CONSTANTS.primary._states.focusVisible}` },
  "&:disabled": { opacity: 0.4, cursor: "default" },
  ...REDUCED_MOTION_SX,
});
